import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, Award, Target, CheckCircle, Clock, BookOpen, Download, 
  Mail, Printer, ArrowLeft, BarChart3, PieChart, Calendar
} from 'lucide-react';
import { designSystem } from '../designSystem';

const MyProgressPage = () => {
  const [progressData, setProgressData] = useState(null);
  const [pillarsContent, setPillarsContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = userData.user_id || userData.id || 'demo_user';
  const userName = userData.full_name || userData.username || 'User';

  useEffect(() => {
    fetchProgressData();
  }, []);

  const fetchProgressData = async () => {
    try {
      const [progressRes, pillarsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/pillars/progress/${userId}/detailed`),
        fetch(`${BACKEND_URL}/api/pillars/all`)
      ]);
      
      const progress = await progressRes.json();
      const pillars = await pillarsRes.json();
      
      setProgressData(progress);
      setPillarsContent(pillars);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching progress:', error);
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const handleShareEmail = async () => {
    if (!emailAddress) {
      alert('Please enter an email address');
      return;
    }

    setSendingEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/pillars/share-progress?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailAddress })
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`Progress report sent to ${emailAddress}`);
        setEmailModalOpen(false);
        setEmailAddress('');
      }
    } catch (error) {
      console.error('Error sharing progress:', error);
      alert('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  const getPillarName = (pillarId) => {
    const pillar = pillarsContent.find(p => p.pillar_id === pillarId);
    return pillar ? pillar.name : pillarId;
  };

  const calculatePillarCompletion = (pillarProgress) => {
    if (!pillarProgress?.modules_progress?.length) return 0;
    const completed = pillarProgress.modules_progress.filter(m => m.completed_at).length;
    return (completed / pillarProgress.modules_progress.length) * 100;
  };

  const getTotalAssessmentScore = () => {
    if (!progressData?.assessments) return { correct: 0, total: 0 };
    let correct = 0;
    let total = 0;
    progressData.assessments.forEach(assessment => {
      assessment.questions.forEach(q => {
        total++;
        if (q.is_correct) correct++;
      });
    });
    return { correct, total };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading Your Progress...</p>
        </div>
      </div>
    );
  }

  const overallCompletion = progressData?.overall_completion_percentage || 0;
  const pillarsCompleted = progressData?.total_pillars_completed || 0;
  const assessmentScore = getTotalAssessmentScore();
  const assessmentPercentage = assessmentScore.total > 0 
    ? Math.round((assessmentScore.correct / assessmentScore.total) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Print Styles */}
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            body {
              background: white !important;
            }
          }
          .print-only {
            display: none;
          }
        `}
      </style>

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-2xl no-print relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <button
                onClick={() => navigate('/learning-modules')}
                className="p-3 hover:bg-white/20 rounded-2xl transition-all backdrop-blur-sm"
              >
                <ArrowLeft className="h-7 w-7" />
              </button>
              <div>
                <h1 className="text-4xl font-extrabold mb-2 tracking-tight" style={{ fontFamily: designSystem.typography.fonts.display, fontSize: designSystem.typography.sizes['4xl'] }}>My Learning Progress</h1>
                <p className="text-blue-50 text-lg font-medium" style={{ fontSize: designSystem.typography.sizes.lg }}>Detailed analytics and achievements</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
              <button
                onClick={handlePrint}
                className="px-6 py-3 bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl hover:bg-white/20 transition-all flex items-center space-x-2 font-bold"
                style={{ fontSize: designSystem.typography.sizes.base }}
              >
                <Printer className="h-6 w-6" />
                <span>Print</span>
              </button>
              <button
                onClick={handleDownloadPDF}
                className="px-6 py-3 bg-white/10 backdrop-blur-sm border-2 border-white/30 rounded-2xl hover:bg-white/20 transition-all flex items-center space-x-2 font-bold"
                style={{ fontSize: designSystem.typography.sizes.base }}
              >
                <Download className="h-6 w-6" />
                <span>Download PDF</span>
              </button>
              <button
                onClick={() => setEmailModalOpen(true)}
                className="px-6 py-3 bg-white text-indigo-600 rounded-2xl hover:bg-gray-50 transition-all shadow-2xl hover:shadow-3xl flex items-center space-x-2 font-bold transform hover:scale-105"
                style={{ fontSize: designSystem.typography.sizes.base }}
              >
                <Mail className="h-6 w-6" />
                <span>Share via Email</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Print Header */}
      <div className="print-only max-w-7xl mx-auto px-4 py-8 border-b-2 border-gray-300">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">6 Pillars Learning Progress Report</h1>
        <p className="text-xl text-gray-600">Student: {userName}</p>
        <p className="text-gray-600">Generated: {new Date().toLocaleDateString()}</p>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-blue-100 transform hover:scale-105">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-blue-500 rounded-2xl shadow-lg">
                <Target className="h-10 w-10 text-white" />
              </div>
              <span className="text-5xl font-extrabold text-blue-600" style={{ fontFamily: designSystem.typography.fonts.display }}>{Math.round(overallCompletion)}%</span>
            </div>
            <h3 className="text-base font-bold text-gray-700 uppercase tracking-wide" style={{ fontSize: designSystem.typography.sizes.sm }}>Overall Completion</h3>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-green-100 transform hover:scale-105">
            <div className="flex items-center justify-between mb-6">
              <div className="p-4 bg-green-500 rounded-2xl shadow-lg">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <span className="text-5xl font-extrabold text-green-600" style={{ fontFamily: designSystem.typography.fonts.display }}>{pillarsCompleted}/8</span>
            </div>
            <h3 className="text-base font-bold text-gray-700 uppercase tracking-wide" style={{ fontSize: designSystem.typography.sizes.sm }}>Pillars Completed</h3>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Award className="h-8 w-8 text-purple-600" />
              </div>
              <span className="text-3xl font-bold text-purple-600">{assessmentPercentage}%</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Assessment Score</h3>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-amber-100 rounded-lg">
                <TrendingUp className="h-8 w-8 text-amber-600" />
              </div>
              <span className="text-3xl font-bold text-amber-600">{progressData?.pillars_progress?.length || 0}</span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 uppercase">Pillars In Progress</h3>
          </div>
        </div>

        {/* Combined Visualization */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Circular Progress Chart */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <PieChart className="h-6 w-6 mr-2 text-blue-600" />
              Overall Progress
            </h3>
            <div className="flex items-center justify-center">
              <div className="relative w-64 h-64">
                <svg className="transform -rotate-90 w-64 h-64">
                  <circle
                    cx="128"
                    cy="128"
                    r="112"
                    stroke="#e5e7eb"
                    strokeWidth="24"
                    fill="transparent"
                  />
                  <circle
                    cx="128"
                    cy="128"
                    r="112"
                    stroke="url(#gradient)"
                    strokeWidth="24"
                    fill="transparent"
                    strokeDasharray={`${2 * Math.PI * 112}`}
                    strokeDashoffset={`${2 * Math.PI * 112 * (1 - overallCompletion / 100)}`}
                    strokeLinecap="round"
                    className="transition-all duration-1000"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex items-center justify-center flex-col">
                  <span className="text-5xl font-bold text-gray-900">{Math.round(overallCompletion)}%</span>
                  <span className="text-gray-600 mt-2">Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bar Chart - Pillar-wise Progress */}
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
              <BarChart3 className="h-6 w-6 mr-2 text-blue-600" />
              Pillar-wise Progress
            </h3>
            <div className="space-y-4">
              {pillarsContent.slice(0, 8).map((pillar, index) => {
                const pillarProg = progressData?.pillars_progress?.find(p => p.pillar_id === pillar.pillar_id);
                const completion = pillarProg ? calculatePillarCompletion(pillarProg) : 0;
                const isComplete = pillarProg?.completed_at;

                return (
                  <div key={pillar.pillar_id}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700 flex items-center">
                        <span className="mr-2">{pillar.icon}</span>
                        Pillar {pillar.pillar_number}
                      </span>
                      <span className="text-sm font-bold text-gray-900">{Math.round(completion)}%</span>
                    </div>
                    <div className="relative w-full bg-gray-200 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full transition-all ${
                          isComplete ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-blue-500 to-indigo-500'
                        }`}
                        style={{ width: `${completion}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Detailed Pillar Breakdown */}
        <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Detailed Pillar Breakdown</h3>
          <div className="space-y-6">
            {progressData?.pillars_progress?.map((pillarProg) => {
              const pillarInfo = pillarsContent.find(p => p.pillar_id === pillarProg.pillar_id);
              if (!pillarInfo) return null;

              const completion = calculatePillarCompletion(pillarProg);
              const completedModules = pillarProg.modules_progress?.filter(m => m.completed_at).length || 0;
              const totalModules = pillarProg.modules_progress?.length || 0;

              return (
                <div key={pillarProg.pillar_id} className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-4xl">{pillarInfo.icon}</span>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">
                          Pillar {pillarInfo.pillar_number}: {pillarInfo.name}
                        </h4>
                        <p className="text-sm text-gray-600">{completedModules}/{totalModules} modules completed</p>
                      </div>
                    </div>
                    {pillarProg.completed_at && (
                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold flex items-center">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Completed
                      </span>
                    )}
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Progress</span>
                      <span className="text-sm font-bold text-gray-900">{Math.round(completion)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full"
                        style={{ width: `${completion}%` }}
                      ></div>
                    </div>
                  </div>

                  {pillarProg.modules_progress && pillarProg.modules_progress.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="font-semibold text-gray-900 mb-3">Module Progress:</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {pillarProg.modules_progress.map((module, idx) => (
                          <div key={module.sub_pillar_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <span className="text-sm text-gray-700">Module {idx + 1}</span>
                            {module.completed_at ? (
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            ) : module.started_at ? (
                              <Clock className="h-5 w-5 text-amber-600" />
                            ) : (
                              <span className="text-xs text-gray-400">Not Started</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Assessment Results */}
        {progressData?.assessments && progressData.assessments.length > 0 && (
          <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Assessment Results</h3>
            <div className="space-y-6">
              {progressData.assessments.map((assessment) => {
                const pillarInfo = pillarsContent.find(p => p.pillar_id === assessment.pillar_id);
                const correct = assessment.questions.filter(q => q.is_correct).length;
                const total = assessment.questions.length;
                const percentage = Math.round((correct / total) * 100);

                return (
                  <div key={`${assessment.pillar_id}_${assessment.sub_pillar_id}`} className="border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-gray-900">
                        {pillarInfo?.name || assessment.pillar_id} - Module Assessment
                      </h4>
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${
                        percentage >= 80 ? 'bg-green-100 text-green-800' :
                        percentage >= 60 ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {correct}/{total} ({percentage}%)
                      </span>
                    </div>
                    <div className="space-y-2">
                      {assessment.questions.map((q, idx) => (
                        <div key={idx} className="flex items-center space-x-3 text-sm">
                          {q.is_correct ? (
                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                          ) : (
                            <span className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-red-600 text-xs">✕</span>
                            </span>
                          )}
                          <span className="text-gray-700">Question {idx + 1}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 no-print">
          <div className="bg-white rounded-xl max-w-md w-full p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Share Progress Report</h3>
            <p className="text-gray-600 mb-6">Enter the email address where you'd like to send your progress report.</p>
            
            <input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="email@example.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-6"
            />

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setEmailModalOpen(false);
                  setEmailAddress('');
                }}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShareEmail}
                disabled={sendingEmail || !emailAddress}
                className={`flex-1 px-6 py-3 rounded-lg flex items-center justify-center space-x-2 transition-all ${
                  sendingEmail || !emailAddress
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-md hover:shadow-lg'
                }`}
              >
                {sendingEmail ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Mail className="h-5 w-5" />
                    <span>Send Email</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProgressPage;
